/**
 * CheckoutComponent
 *
 * A self-contained payment checkout page that works both:
 *  - Embedded in a React Native WebView (passes results back via postMessage)
 *  - As a standalone page on the website
 *
 * URL params (all URL-encoded; objects are JSON-encoded before encoding):
 *   token            – Bearer auth token for backend calls
 *   event_id         – Event ID (omit for standalone donations)
 *   recipient_user_id– User ID receiving the funds (donations with no event_id)
 *   tickets          – JSON array: [{ticket_id, quantity, is_standing?,
 *                        seats?:[{seat_id,row_id,section_id,seat_number,row_name,
 *                        section_name,gate,zone,level,table_name}]}]
 *   amount           – Total amount (float as string)
 *   currency         – 3-char ISO code, e.g. "EUR"
 *   description      – Line-item label shown on payment provider page
 *   promotion_id     – Optional promotion ID
 *   survey_answers   – JSON array: [{question:string, answer:boolean}]
 *   is_donation      – "true" | "false"
 *   donation_message – Optional freetext message for donations
 *   platform_fee     – Marketplace fee deducted before merchant payout (float as string)
 *   transfer_date    – ISO date (YYYY-MM-DD) when funds are released; omit for immediate
 *   mode             – "app" (postMessage) | "web" (show result page)
 *   payment_method   – 1 (PayPal direct, dormant) | 2 (Stripe — card, PayPal,
 *                        Klarna, wallets via the Payment Element; default)
 *
 * Payment flow (both methods):
 *   Stripe:  createIntent → Stripe Payment Element → finalizeIntent
 *            Redirect methods (PayPal/Klarna/3DS) come back to this page with
 *            ?payment_intent=…&redirect_status=… — handled BEFORE auto-init so
 *            no second intent is created. The backend webhook
 *            (payment_intent.succeeded) is the fulfillment source of truth;
 *            finalize here is the fast path. postMessage may also emit
 *            status:'pending' when confirmation is still in flight.
 *   PayPal:  PayPal JS SDK Buttons → createOrder → captureOrder (server-side)
 *            (kept for a possible later split; not used while Stripe manages all)
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { loadStripe, StripeElementsOptions } from '@stripe/stripe-js';
import {
  Elements,
  ExpressCheckoutElement,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';

// ─── Constants ──────────────────────────────────────────────────────────────
const API_BASE = 'https://maineventbackend.seuytel.com/api';

// ─── Constants ────────────────────────────────────────────────────────────────
/**
 * Platform fee charged on donations: a flat 10% of the donation amount.
 * (Tickets carry their own app-computed platform_fee; only donations are
 * recomputed here because their amount is entered on this page.)
 */
const DONATION_FEE_RATE = 0.10;

// ─── Types ───────────────────────────────────────────────────────────────────
/**
 * Snake-case seat shape forwarded to the backend (see app's toSeatPayload).
 * Venue seats carry seat_id/row_id/section_id (+ seat_number/gate/zone/level);
 * table seats carry table_name + seat_number. All are optional/pass-through.
 */
interface SeatLine {
  ticket_id?: number;
  seat_id?: string;
  row_id?: string;
  section_id?: string;
  seat_number?: string;
  row_name?: string;
  section_name?: string;
  gate?: string;
  zone?: string;
  level?: string;
  /** Table reservation only — the table's name (empty/absent for venue seats). */
  table_name?: string;
}

interface TicketLine {
  ticket_id: number;
  quantity: number;
  seats?: SeatLine[];
  /** Standing-block tickets carry no seats — quantity is the count. */
  is_standing?: boolean;
}

/** Flat platform fee on a donation of `amount` (rounded to cents). */
function donationPlatformFee(amount: number): number {
  return amount > 0 ? Math.round(amount * DONATION_FEE_RATE * 100) / 100 : 0;
}

interface SurveyAnswer {
  question: string;
  answer: boolean | string;
}

interface CheckoutParams {
  token: string;
  event_id: number | null;
  /** User ID receiving funds for donations not linked to an event */
  recipient_user_id: number | null;
  tickets: TicketLine[];
  amount: number;
  currency: string;
  description: string;
  promotion_id: number | null;
  survey_answers: SurveyAnswer[];
  is_donation: boolean;
  donation_message: string;
  /** Marketplace fee deducted before releasing funds to the merchant */
  platform_fee: number | null;
  /** ISO date when funds are released to the merchant; null = immediate */
  transfer_date: string | null;
  mode: 'app' | 'web';
  /** 1 = PayPal, 2 = Stripe/card */
  payment_method: 1 | 2;
  /**
   * Set when the RN app's own per-method button was tapped (not a generic "Pay"
   * button) — restricts the Stripe intent to exactly that method, so this page
   * shows only that method's form instead of the full tabbed picker. Omit for
   * the generic Stripe Payment Element (all enabled methods as tabs).
   */
  payment_method_type: PaymentMethodType | null;
}

type PaymentMethodType = 'card' | 'paypal' | 'klarna' | 'apple_pay' | 'google_pay' | 'link';

interface PaymentConfig {
  stripe_publishable_key: string;
  paypal_client_id: string;
  paypal_mode: 'sandbox' | 'live';
}

/** payment_method values: 1 = PayPal, 2 = Stripe/card */
type Phase = 'init' | 'stripe' | 'paypal' | 'success' | 'pending' | 'error';

const VALID_METHOD_TYPES: PaymentMethodType[] = ['card', 'paypal', 'klarna', 'apple_pay', 'google_pay', 'link'];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function parseParams(): CheckoutParams {
  const p = new URLSearchParams(window.location.search);

  const safe = (key: string, fallback = '') => p.get(key) ?? fallback;

  let tickets: TicketLine[] = [];
  try { tickets = JSON.parse(decodeURIComponent(safe('tickets', '[]'))); } catch { /* empty */ }

  let surveyAnswers: SurveyAnswer[] = [];
  try { surveyAnswers = JSON.parse(decodeURIComponent(safe('survey_answers', '[]'))); } catch { /* empty */ }

  return {
    token:             safe('token'),
    event_id:          p.get('event_id') ? Number(p.get('event_id')) : null,
    recipient_user_id: p.get('recipient_user_id') ? Number(p.get('recipient_user_id')) : null,
    tickets,
    amount:            parseFloat(safe('amount', '0')),
    currency:          safe('currency', 'EUR').toUpperCase(),
    description:       safe('description', 'Event Ticket'),
    promotion_id:      p.get('promotion_id') ? Number(p.get('promotion_id')) : null,
    survey_answers:    surveyAnswers,
    is_donation:       safe('is_donation') === 'true',
    donation_message:  safe('donation_message'),
    platform_fee:      p.get('platform_fee') ? parseFloat(p.get('platform_fee')!) : null,
    transfer_date:     p.get('transfer_date') ?? null,
    mode:              safe('mode', 'web') as 'app' | 'web',
    payment_method:    (p.get('payment_method') === '1' ? 1 : 2) as 1 | 2,
    payment_method_type: (VALID_METHOD_TYPES as string[]).includes(safe('payment_method_type'))
      ? (safe('payment_method_type') as PaymentMethodType)
      : null,
  };
}

/** Heading for the Stripe screen, tailored to the method the RN app requested */
function stripeHeading(methodType: PaymentMethodType | null): string {
  switch (methodType) {
    case 'paypal':     return 'PayPal Payment';
    case 'klarna':      return 'Klarna Payment';
    case 'apple_pay':   return 'Apple Pay';
    case 'google_pay':  return 'Google Pay';
    case 'card':        return 'Card Payment';
    default:            return 'Card Payment';
  }
}

interface WalletDiagnostics {
  host: string;
  secureContext: boolean;
  paymentRequest: boolean;
  isAndroid: boolean;
  isChrome: boolean;
  isWebView: boolean;
  googlePaySupportedMarker: boolean;
}

function googlePayUnavailableMessage(diagnostics: WalletDiagnostics | null): string {
  if (diagnostics?.isWebView) {
    return diagnostics.googlePaySupportedMarker
      ? 'Google Pay setup in this WebView looks OK, but Stripe/Google did not expose the button.'
      : 'Google Pay is not available in this Android WebView because the app WebView marker is missing.';
  }

  return 'Google Pay is not available for this payment in Stripe on this device or browser.';
}

function googlePayDiagnosticsText(
  diagnostics: WalletDiagnostics,
  walletAvailabilityDetails: string,
  intentId: string,
  amount: number,
  currency: string,
  methodType: PaymentMethodType | null,
): string {
  const surface = diagnostics.isWebView ? 'WebView' : diagnostics.isChrome ? 'Chrome' : 'not Chrome';
  const marker = diagnostics.isWebView
    ? diagnostics.googlePaySupportedMarker
      ? 'Google Pay WebView marker ok'
      : 'Google Pay WebView marker missing'
    : 'WebView marker not needed';

  return [
    `Host: ${diagnostics.host}`,
    diagnostics.isAndroid ? 'Android' : 'not Android',
    surface,
    diagnostics.secureContext ? 'HTTPS ok' : 'HTTPS missing',
    diagnostics.paymentRequest ? 'PaymentRequest ok' : 'PaymentRequest missing',
    marker,
    `Method: ${methodType || 'auto'}`,
    `Amount: ${currency} ${amount.toFixed(2)}`,
    `Intent: ${intentId || 'unknown'}`,
    `Stripe available: ${walletAvailabilityDetails || 'unknown'}`,
  ].join(' · ');
}

/** Send result to React Native WebView or do nothing if in web mode */
function postResult(mode: 'app' | 'web', payload: object) {
  if (mode === 'app' && (window as any).ReactNativeWebView) {
    (window as any).ReactNativeWebView.postMessage(JSON.stringify(payload));
  }
}

/**
 * Redirect methods such as Klarna cap `return_url` length. The checkout page URL
 * can be huge for tickets because it contains the cart JSON, survey answers and
 * token. The backend already persisted the cart against the PaymentIntent, so the
 * redirect return only needs enough state to finalize that intent.
 */
function stripeReturnUrl(params: CheckoutParams): string {
  const url = new URL(window.location.origin + window.location.pathname);
  url.searchParams.set('token', params.token);
  url.searchParams.set('mode', params.mode);
  url.searchParams.set('payment_method', String(params.payment_method));

  const current = new URLSearchParams(window.location.search);
  ['webview', 'platform'].forEach(key => {
    const value = current.get(key);
    if (value) url.searchParams.set(key, value);
  });

  return url.toString();
}

/**
 * Server-side finalize of a Stripe PaymentIntent (fast path — the backend
 * webhook fulfills regardless). `pending` = Stripe still shows the intent as
 * processing, common right after redirect-method returns (PayPal/Klarna).
 */
async function finalizeIntentOnServer(
  token: string,
  piId: string,
): Promise<{ ok: boolean; txId?: number; message?: string; pending?: boolean }> {
  try {
    const res = await fetch(`${API_BASE}/payment/stripe/intent/finalize`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', Authorization: `Bearer ${token}` },
      body:    JSON.stringify({ intent_id: piId }),
    });
    const data = await res.json();
    if (data.status === 'success') return { ok: true, txId: data.transaction_id };
    const message = data.message ?? 'Finalization failed';
    return { ok: false, message, pending: /processing|pending/i.test(message) };
  } catch {
    return { ok: false, message: 'Network error. Please try again.' };
  }
}

// ─── Stripe inner form ───────────────────────────────────────────────────────
interface StripeFormProps {
  params: CheckoutParams;
  intentId: string;
  clientSecret: string;
  /** The method the intent was restricted to (backend-echoed), or null for the generic picker */
  methodType: PaymentMethodType | null;
  onSuccess: (txId: number) => void;
  onError: (msg: string) => void;
}

function StripeForm({ params, intentId, clientSecret, methodType, onSuccess, onError }: StripeFormProps) {
  const stripe   = useStripe();
  const elements = useElements();
  const [busy, setBusy]         = useState(false);
  const [errMsg, setErrMsg]     = useState('');
  const [walletAvailable, setWalletAvailable] = useState<boolean | null>(null);
  const [walletAvailabilityDetails, setWalletAvailabilityDetails] = useState('');

  // Redirect-backs from Stripe (3DS / PayPal / Klarna) never reach this form:
  // the parent intercepts ?payment_intent=… before auto-init and finalizes the
  // original intent directly — mounting the Payment Element here would create
  // a second, orphaned intent.

  async function finalizeOnServer(piId: string) {
    setBusy(true);
    const result = await finalizeIntentOnServer(params.token, piId);
    if (result.ok) {
      onSuccess(result.txId!);
    } else {
      onError(result.message ?? 'Finalization failed');
    }
    setBusy(false);
  }

  const isWalletMethod = methodType === 'apple_pay' || methodType === 'google_pay';
  const walletDiagnostics = (() => {
    if (typeof window === 'undefined') return null;
    const ua = window.navigator.userAgent || '';
    const isAndroid = /Android/i.test(ua);
    const isWebView = /; wv\)|Version\/\d+\.\d+.*Chrome\//i.test(ua);
    const isChrome = /Chrome\//i.test(ua) && !/Edg\//i.test(ua);
    const googlePaySupportedMarker = /GOOGLE_PAY_SUPPORTED/i.test(ua);
    return {
      host: window.location.hostname,
      secureContext: window.isSecureContext,
      paymentRequest: 'PaymentRequest' in window,
      isAndroid,
      isChrome,
      isWebView,
      googlePaySupportedMarker,
    };
  })();

  async function confirmElementsPayment() {
    if (!stripe || !elements || busy) return;
    setBusy(true);
    setErrMsg('');

    // Express Checkout wallets need submit() before confirmPayment(), so Stripe
    // can validate and collect the wallet details held by the Element.
    const { error: submitError } = await elements.submit();
    if (submitError) {
      setErrMsg(submitError.message ?? 'Payment details could not be submitted.');
      setBusy(false);
      return;
    }

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      clientSecret,
      confirmParams: {
        return_url: stripeReturnUrl(params),
      },
      redirect: 'if_required',
    });

    if (error) {
      setErrMsg(error.message ?? 'Payment failed');
      setBusy(false);
      return;
    }

    if (!paymentIntent || paymentIntent.status === 'succeeded') {
      await finalizeOnServer(paymentIntent?.id ?? intentId);
    } else {
      setErrMsg('Payment is pending or incomplete. Please try again.');
      setBusy(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!stripe || !elements || busy) return;
    await confirmElementsPayment();
  }

  if (isWalletMethod) {
    return (
      <div style={styles.form}>
        <ExpressCheckoutElement
          options={{
            paymentMethods: {
              applePay: methodType === 'apple_pay' ? 'always' : 'never',
              googlePay: methodType === 'google_pay' ? 'always' : 'never',
              link: 'never',
              paypal: 'never',
              amazonPay: 'never',
            },
            buttonType: {
              applePay: 'check-out',
              googlePay: 'checkout',
            },
            buttonTheme: {
              applePay: 'black',
            },
            buttonHeight: 50,
          }}
          onReady={(event) => {
            const available = (event.availablePaymentMethods ?? {}) as {
              applePay?: boolean;
              googlePay?: boolean;
            };
            setWalletAvailabilityDetails(
              Object.keys(available).length > 0
                ? Object.keys(available).join(', ')
                : 'none',
            );
            setWalletAvailable(
              methodType === 'apple_pay'
                ? !!available.applePay
                : !!available.googlePay,
            );
          }}
          onConfirm={confirmElementsPayment}
        />
        {walletAvailable === false && (
          <div style={styles.errorText}>
            <p style={{ margin: 0 }}>
              {methodType === 'apple_pay'
                ? 'Apple Pay is not available on this device or browser.'
                : googlePayUnavailableMessage(walletDiagnostics)}
            </p>
            {methodType === 'google_pay' && walletDiagnostics && (
              <p style={{ margin: '8px 0 0', fontSize: 12, lineHeight: 1.45 }}>
                Diagnostics: {googlePayDiagnosticsText(
                  walletDiagnostics,
                  walletAvailabilityDetails,
                  intentId,
                  params.amount,
                  params.currency,
                  methodType,
                )}
              </p>
            )}
          </div>
        )}
        {errMsg && <p style={styles.errorText}>{errMsg}</p>}
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} style={styles.form}>
      <PaymentElement
        options={{
          layout: 'tabs',
          // Wallets are rendered above with ExpressCheckoutElement. Keep this
          // PaymentElement focused on card/Klarna/etc. so Apple Pay never falls
          // back to a plain card form when the app requested a wallet.
          wallets: {
            applePay: 'never',
            googlePay: 'never',
          },
        }}
      />
      {errMsg && <p style={styles.errorText}>{errMsg}</p>}
      {/* Disable on !elements too — handleSubmit returns silently without it,
          which reads as "clicked Pay and nothing happened" */}
      <button type="submit" disabled={!stripe || !elements || busy} style={{ ...styles.payBtn, ...((!stripe || !elements || busy) ? styles.payBtnDisabled : {}) }}>
        {busy ? 'Processing…' : `Pay ${params.currency} ${params.amount.toFixed(2)}`}
      </button>
    </form>
  );
}

// ─── PayPal helpers ───────────────────────────────────────────────────────────

/** Inject the PayPal JS SDK script (idempotent) */
function loadPayPalSDK(clientId: string, currency: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if ((window as any).paypal) { resolve(); return; }
    const existing = document.getElementById('paypal-sdk') as HTMLScriptElement | null;
    if (existing) {
      // A tag exists but window.paypal isn't set: either it's still loading, or a
      // previous attempt FAILED. A failed tag never fires 'load', which used to
      // leave this promise pending forever — every retry (Try Again / pay click)
      // then silently did nothing. Remove dead tags and re-inject instead.
      const stillLoading = !(existing as any).__failed;
      if (stillLoading) {
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', () => {
          (existing as any).__failed = true;
          existing.remove();
          reject(new Error('Failed to load PayPal SDK'));
        });
        return;
      }
      existing.remove();
    }

    const script = document.createElement('script');
    script.id = 'paypal-sdk';
    // components=buttons restricts to PayPal buttons only (no card/SEPA fields)
    // disable-funding removes alternative payment methods from the button list
    script.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(clientId)}&currency=${currency}&intent=capture&commit=true&components=buttons&disable-funding=card,sepa,giropay,sofort,bancontact,eps,ideal,mybank,p24,blik`;
    script.onload  = () => resolve();
    script.onerror = () => {
      // Mark + remove the dead tag so the next attempt re-injects a fresh one
      (script as any).__failed = true;
      script.remove();
      reject(new Error('Failed to load PayPal SDK'));
    };
    document.head.appendChild(script);
  });
}

// ─── PayPal Buttons wrapper ───────────────────────────────────────────────────
interface PayPalButtonsProps {
  params: CheckoutParams;
  paypalOrderId: React.MutableRefObject<string | null>;
  onSuccess: (txId: number) => void;
  onError: (msg: string) => void;
  onCancel: () => void;
}

function PayPalButtonsWidget({ params, paypalOrderId, onSuccess, onError, onCancel }: PayPalButtonsProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  // Use a DOM ref for the loading message so hiding it never triggers a re-render.
  // A re-render between pp.Buttons().render() starting and PayPal finishing its
  // async iframe injection causes "Detected container element removed from DOM".
  const msgRef = useRef<HTMLParagraphElement>(null);
  // React 18 Strict Mode invokes effects twice (mount → simulated unmount → remount).
  // Refs survive the simulated unmount, so this flag prevents a second buttons.render()
  // call while the first async render is still in progress — which is exactly what
  // triggers the PayPal "Detected container element removed from DOM" error.
  const didRender = useRef(false);

  useEffect(() => {
    if (didRender.current) return;   // already rendering/rendered — skip
    didRender.current = true;        // set synchronously before any await

    let mounted = true;

    async function init() {
      const pp = (window as any).paypal;
      if (!pp) {
        if (msgRef.current) msgRef.current.textContent = 'PayPal SDK not loaded';
        return;
      }

      const buttons = pp.Buttons({
        // Show only the PayPal button (no Venmo, no card, no SEPA)
        fundingSource: pp.FUNDING.PAYPAL,
        style: { layout: 'vertical', color: 'blue', shape: 'rect', label: 'pay' },

        createOrder: async () => {
          const body: any = {
            amount:            params.amount,
            currency:          params.currency,
            description:       params.description,
            promotion_id:      params.promotion_id,
            survey_answers:    params.survey_answers,
            is_donation:       params.is_donation,
            donation_message:  params.donation_message,
            // Donations: flat 10% recomputed from the amount entered on this page
            // (params.amount is already the effective amount here). Tickets: trust
            // the app-computed fee.
            platform_fee:      (params.is_donation ? donationPlatformFee(params.amount) : params.platform_fee) ?? undefined,
            transfer_date:     params.transfer_date ?? undefined,
          };
          if (params.event_id)          body.event_id = params.event_id;
          if (params.recipient_user_id) body.recipient_user_id = params.recipient_user_id;
          if (params.tickets.length)    body.tickets = params.tickets;

          const res  = await fetch(`${API_BASE}/payment/paypal/order`, {
            method:  'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', Authorization: `Bearer ${params.token}` },
            body:    JSON.stringify(body),
          });
          const data = await res.json();
          if (data.status !== 'success') throw new Error(data.message ?? 'Order creation failed');
          paypalOrderId.current = data.paypal_order_id;
          return data.paypal_order_id;
        },

        onApprove: async (data: { orderID: string }) => {
          try {
            const res  = await fetch(`${API_BASE}/payment/paypal/order/capture`, {
              method:  'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', Authorization: `Bearer ${params.token}` },
              body:    JSON.stringify({ paypal_order_id: data.orderID }),
            });
            const result = await res.json();
            if (result.status === 'success') {
              if (mounted) onSuccess(result.transaction_id);
            } else {
              if (mounted) onError(result.message ?? 'Capture failed');
            }
          } catch {
            if (mounted) onError('Network error during capture');
          }
        },

        onCancel: () => {
          if (mounted) {
            if (paypalOrderId.current) {
              fetch(`${API_BASE}/payment/paypal/order/cancel`, {
                method:  'POST',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', Authorization: `Bearer ${params.token}` },
                body:    JSON.stringify({ paypal_order_id: paypalOrderId.current }),
              }).catch(() => {/* best-effort */});
            }
            onCancel();
          }
        },

        onError: (err: any) => {
          const msg = (err instanceof Error ? err.message : null)
            ?? (typeof err === 'string' ? err : null)
            ?? 'PayPal encountered an error. Please try again.';
          console.error('PayPal SDK error', err);
          if (mounted) onError(msg);
        },
      });

      if (containerRef.current && mounted) {
        containerRef.current.innerHTML = '';
        if (msgRef.current) msgRef.current.style.display = 'none';
        buttons.render(containerRef.current);
      }
    }

    init();
    return () => { mounted = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <p ref={msgRef} style={{ textAlign: 'center', color: '#555', fontSize: 14, margin: '12px 0' }}>
        Loading PayPal…
      </p>
      <div ref={containerRef} style={{ minHeight: 48 }} />
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────
export default function CheckoutComponent() {
  const params = useRef<CheckoutParams>(parseParams()).current;

  const [config, setConfig]         = useState<PaymentConfig | null>(null);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [configError, setConfigError] = useState('');

  const [phase, setPhase]           = useState<Phase>('init');

  // Stripe intent state
  const [stripePromise, setStripePromise] = useState<ReturnType<typeof loadStripe> | null>(null);
  const [clientSecret, setClientSecret]   = useState('');
  // Method the intent was actually restricted to (backend-echoed) — drives
  // the Payment Element's `wallets` option so only the requested wallet
  // button (Apple Pay / Google Pay) is prioritized, or neither for card/paypal/klarna.
  const [resolvedMethodType, setResolvedMethodType] = useState<PaymentMethodType | null>(null);
  const [intentId, setIntentId]           = useState('');
  const [stripeLoading, setStripeLoading] = useState(false);
  const [stripeError, setStripeError]     = useState('');

  // PayPal SDK load state
  const [paypalReady, setPaypalReady]     = useState(false);
  const [paypalError, setPaypalError]     = useState('');
  const paypalOrderId                     = useRef<string | null>(null);

  // Result state
  const [transactionId, setTransactionId] = useState<number | null>(null);
  const [errorMessage, setErrorMessage]   = useState('');

  // True while finalizing a redirect-return (PayPal/Klarna/3DS came back)
  const [finalizing, setFinalizing]       = useState(false);

  // Donation amount override
  const [donationAmount, setDonationAmount] = useState(params.amount > 0 ? params.amount.toFixed(2) : '');

  const effectiveAmount = params.is_donation
    ? parseFloat(donationAmount || '0')
    : params.amount;

  // Donations set their amount on THIS page, so the platform_fee the app computed
  // (on the original amount, effectively 0 for donations) is stale — recompute a
  // flat 10% from the amount actually being charged. Tickets have a fixed amount,
  // so the app-provided fee is already correct.
  const effectivePlatformFee = params.is_donation
    ? donationPlatformFee(effectiveAmount)
    : params.platform_fee;

  // ── Load public payment config on mount ────────────────────────────────
  useEffect(() => {
    fetch(`${API_BASE}/payment/public-config`)
      .then(r => r.json())
      .then((data: PaymentConfig) => {
        setConfig(data);
        // Only load Stripe.js when the user is actually paying by card.
        // Loading it in PayPal mode just adds noise (blocked analytics requests)
        // and makes the console look like the SDKs are interfering with each other.
        if (params.payment_method === 2) {
          setStripePromise(loadStripe(data.stripe_publishable_key));
        }
      })
      .catch(() => setConfigError('Could not load payment configuration. Please refresh.'))
      .finally(() => setLoadingConfig(false));
  }, []);

  // ── Auto-init: skip method selection, go straight to the provider ──────
  useEffect(() => {
    if (loadingConfig || configError || phase !== 'init') return;

    // Redirect-return (PayPal/Klarna/3DS)? The user already paid (or failed) —
    // finalize THAT intent. Never fall through to initStripe(), which would
    // create a fresh, orphaned PaymentIntent.
    const sp = new URLSearchParams(window.location.search);
    const returningIntent = sp.get('payment_intent');
    if (returningIntent) {
      handleRedirectReturn(returningIntent, sp.get('redirect_status'));
      return;
    }

    if (params.payment_method === 1) {
      initPayPal();
    } else {
      initStripe();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingConfig, configError, phase]);

  // ── Initialise Stripe Payment Element ─────────────────────────────────
  const initStripe = useCallback(async () => {
    if (!params.token) { setStripeError('Authentication required'); return; }
    if (effectiveAmount < 0.50) { setStripeError('Amount must be at least 0.50'); return; }
    setStripeLoading(true);
    setStripeError('');

    const body: any = {
      amount:            effectiveAmount,
      currency:          params.currency,
      description:       params.description,
      promotion_id:      params.promotion_id,
      survey_answers:    params.survey_answers,
      is_donation:       params.is_donation,
      donation_message:  params.donation_message,
      platform_fee:      effectivePlatformFee ?? undefined,
      transfer_date:     params.transfer_date ?? undefined,
    };
    if (params.event_id)          body.event_id = params.event_id;
    if (params.recipient_user_id) body.recipient_user_id = params.recipient_user_id;
    if (params.tickets.length)    body.tickets = params.tickets;
    if (params.payment_method_type) body.payment_method_type = params.payment_method_type;

    try {
      const res  = await fetch(`${API_BASE}/payment/stripe/intent`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', Authorization: `Bearer ${params.token}` },
        body:    JSON.stringify(body),
      });
      const data = await res.json();
      if (data.status !== 'success') { setStripeError(data.message ?? 'Could not initialise payment'); return; }
      setClientSecret(data.client_secret);
      setIntentId(data.intent_id);
      // Echoed back from the backend — drives which wallet button (if any) the
      // Payment Element prioritizes. Falls back to the URL param if the backend
      // ever omits it (e.g. older deploy), so behavior degrades gracefully.
      setResolvedMethodType(data.payment_method_type ?? params.payment_method_type ?? null);
      setPhase('stripe');
    } catch {
      setStripeError('Network error. Please try again.');
    } finally {
      setStripeLoading(false);
    }
  }, [params, effectiveAmount, effectivePlatformFee]);

  // ── Initialise PayPal SDK ──────────────────────────────────────────────
  const initPayPal = useCallback(async () => {
    if (!config) return;
    if (effectiveAmount <= 0) { setPaypalError('Enter a valid amount'); return; }
    setPaypalError('');
    try {
      await loadPayPalSDK(config.paypal_client_id, params.currency);
      setPaypalReady(true);
      setPhase('paypal');
    } catch (e: any) {
      setPaypalError(e.message ?? 'Could not load PayPal');
    }
  }, [config, params.currency, effectiveAmount]);

  // ── Result handlers ────────────────────────────────────────────────────
  const handleSuccess = useCallback((txId: number) => {
    setTransactionId(txId);
    setPhase('success');
    postResult(params.mode, { status: 'success', transaction_id: txId });
  }, [params.mode]);

  const handleError = useCallback((msg: string) => {
    setErrorMessage(msg);
    setPhase('error');
    postResult(params.mode, { status: 'error', message: msg });
  }, [params.mode]);

  const handleCancel = useCallback(() => {
    setErrorMessage('Payment was cancelled.');
    setPhase('error');
    postResult(params.mode, { status: 'error', message: 'Payment was cancelled.' });
  }, [params.mode]);

  // ── Redirect-return: finalize the intent the user just paid ────────────
  const handleRedirectReturn = useCallback(async (piId: string, redirectStatus: string | null) => {
    // Consume the redirect params so "Try Again" after a failure starts a
    // clean payment instead of re-finalizing this intent forever.
    const url = new URL(window.location.href);
    ['payment_intent', 'payment_intent_client_secret', 'redirect_status', 'source_type'].forEach(k => url.searchParams.delete(k));
    window.history.replaceState(null, '', url.toString());

    if (redirectStatus && redirectStatus !== 'succeeded' && redirectStatus !== 'processing') {
      handleError('Payment was not completed. Please try again.');
      return;
    }

    setFinalizing(true);
    // Redirect methods can land back while Stripe still reports 'processing' —
    // retry briefly; after that the backend webhook fulfills on its own.
    for (let attempt = 0; attempt < 3; attempt++) {
      const result = await finalizeIntentOnServer(params.token, piId);
      if (result.ok) {
        setFinalizing(false);
        handleSuccess(result.txId!);
        return;
      }
      if (!result.pending) {
        setFinalizing(false);
        handleError(result.message ?? 'Finalization failed');
        return;
      }
      await new Promise(r => setTimeout(r, 2500));
    }
    setFinalizing(false);
    setPhase('pending');
    postResult(params.mode, { status: 'pending', payment_intent: piId });
  }, [params.token, params.mode, handleSuccess, handleError]);

  // ─── Render ──────────────────────────────────────────────────────────────
  if (loadingConfig) {
    return <div style={styles.center}><Spinner /><p style={styles.loadingText}>Loading…</p></div>;
  }
  if (configError) {
    return <div style={styles.center}><p style={styles.errorText}>{configError}</p></div>;
  }
  if (finalizing) {
    return <div style={styles.center}><Spinner /><p style={styles.loadingText}>Confirming payment…</p></div>;
  }

  // ── Order summary helper ───────────────────────────────────────────────
  const orderSummary = (
    <div style={styles.summaryBox}>
      <p style={styles.summaryLabel}>
        {params.is_donation ? 'Donation' : params.description || 'Event Ticket'}
      </p>
      {params.is_donation ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
          <span style={styles.currencyBadge}>{params.currency}</span>
          <input
            type="number"
            min="1"
            step="0.01"
            value={donationAmount}
            onChange={e => setDonationAmount(e.target.value)}
            placeholder="Enter amount"
            style={styles.donationInput}
          />
        </div>
      ) : (
        <p style={styles.totalAmount}>
          {params.currency} {params.amount.toFixed(2)}
        </p>
      )}
    </div>
  );

  // ── Success screen ─────────────────────────────────────────────────────
  if (phase === 'success') {
    return (
      <div style={styles.page}>
        <div style={styles.resultCard}>
          <div style={styles.successIcon}>✓</div>
          <h2 style={styles.resultTitle}>Payment Successful!</h2>
          <p style={styles.resultBody}>
            {params.is_donation
              ? 'Thank you for your donation!'
              : 'Your ticket has been confirmed. Check your profile to view it.'}
          </p>
          {transactionId && (
            <p style={styles.txId}>Transaction #{transactionId}</p>
          )}
          {params.mode === 'web' && (
            <button style={styles.payBtn} onClick={() => window.history.back()}>
              Back
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── Pending screen (redirect method still processing — webhook will fulfill) ──
  if (phase === 'pending') {
    return (
      <div style={styles.page}>
        <div style={styles.resultCard}>
          <div style={styles.pendingIcon}>⏳</div>
          <h2 style={styles.resultTitle}>Payment Processing</h2>
          <p style={styles.resultBody}>
            Your payment is being confirmed by the payment provider.
            {params.is_donation
              ? ' Your donation will be recorded automatically'
              : ' Your ticket will be issued automatically'}
            {' '}once it completes — you do not need to pay again.
          </p>
          {params.mode === 'web' && (
            <button style={styles.payBtn} onClick={() => window.history.back()}>
              Back
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── Error screen ───────────────────────────────────────────────────────
  if (phase === 'error') {
    return (
      <div style={styles.page}>
        <div style={styles.resultCard}>
          <div style={styles.errorIcon}>✕</div>
          <h2 style={styles.resultTitle}>Payment Failed</h2>
          <p style={styles.resultBody}>{errorMessage}</p>
          <button style={styles.payBtn} onClick={() => setPhase('init')}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // ── Stripe Payment Element screen ──────────────────────────────────────
  if (phase === 'stripe' && stripePromise && clientSecret) {
    const elementsOptions: StripeElementsOptions = {
      clientSecret,
      appearance: {
        theme: 'stripe',
        variables: {
          colorPrimary:    '#5e06a7',
          colorBackground: '#ffffff',
          borderRadius:    '8px',
        },
      },
    };
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <h1 style={styles.heading}>{stripeHeading(resolvedMethodType)}</h1>
          {orderSummary}
          <Elements stripe={stripePromise} options={elementsOptions}>
            <StripeForm
              params={{ ...params, amount: effectiveAmount }}
              intentId={intentId}
              clientSecret={clientSecret}
              methodType={resolvedMethodType}
              onSuccess={handleSuccess}
              onError={handleError}
            />
          </Elements>
          <LockBadge />
        </div>
      </div>
    );
  }

  // ── PayPal Buttons screen ──────────────────────────────────────────────
  if (phase === 'paypal' && paypalReady) {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <h1 style={styles.heading}>PayPal Checkout</h1>
          {orderSummary}
          {paypalError && <p style={styles.errorText}>{paypalError}</p>}
          <PayPalButtonsWidget
            params={{ ...params, amount: effectiveAmount }}
            paypalOrderId={paypalOrderId}
            onSuccess={handleSuccess}
            onError={handleError}
            onCancel={handleCancel}
          />
          <LockBadge />
        </div>
      </div>
    );
  }

  // ── init / loading state (config loading or auto-init in progress) ────
  if (stripeLoading) {
    return <div style={styles.center}><Spinner /><p style={styles.loadingText}>Preparing payment…</p></div>;
  }

  if (phase === 'init') {
    // initStripe() failed — show the error so the user isn't stuck on the spinner
    if (stripeError || paypalError) {
      const errMsg = stripeError || paypalError;
      return (
        <div style={styles.center}>
          <p style={styles.errorText}>{errMsg}</p>
          <button
            style={{ ...styles.payBtn, marginTop: 16 }}
            onClick={() => params.payment_method === 1 ? initPayPal() : initStripe()}
          >
            Try Again
          </button>
        </div>
      );
    }
    return <div style={styles.center}><Spinner /><p style={styles.loadingText}>Preparing payment…</p></div>;
  }

  // Fallback: should never reach here
  return <div style={styles.center}><Spinner /></div>;
}

// ─── Sub-components ───────────────────────────────────────────────────────────
function Spinner() {
  return (
    <div style={{
      width: 32, height: 32, border: '3px solid #e8d5ff',
      borderTopColor: '#5e06a7', borderRadius: '50%',
      animation: 'spin 0.8s linear infinite',
    }} />
  );
}

function LockBadge() {
  return (
    <p style={styles.lockBadge}>🔒 Payments are encrypted and secure</p>
  );
}

// ─── Inline styles ────────────────────────────────────────────────────────────
const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f5f0ff 0%, #ede8f8 100%)',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: '24px 16px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  center: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  container: {
    background: '#ffffff',
    borderRadius: 20,
    boxShadow: '0 8px 40px rgba(94,6,167,0.12)',
    padding: '28px 24px',
    width: '100%',
    maxWidth: 440,
  },
  heading: {
    fontSize: 22,
    fontWeight: 800,
    color: '#3d0080',
    margin: '0 0 20px 0',
    textAlign: 'center',
  },
  summaryBox: {
    background: '#f8f4ff',
    border: '1px solid #e8d5ff',
    borderRadius: 12,
    padding: '14px 16px',
    marginBottom: 20,
  },
  summaryLabel: {
    fontSize: 13,
    color: '#7c3aed',
    fontWeight: 600,
    margin: 0,
  },
  totalAmount: {
    fontSize: 22,
    fontWeight: 800,
    color: '#3d0080',
    margin: '6px 0 0 0',
  },
  currencyBadge: {
    fontSize: 13,
    fontWeight: 700,
    color: '#7c3aed',
    background: '#f0e8ff',
    borderRadius: 6,
    padding: '4px 8px',
  },
  donationInput: {
    flex: 1,
    fontSize: 20,
    fontWeight: 700,
    color: '#3d0080',
    border: 'none',
    borderBottom: '2px solid #7c3aed',
    background: 'transparent',
    outline: 'none',
    padding: '4px 0',
    width: '100%',
    maxWidth: 200,
  },
  sectionLabel: {
    fontSize: 13,
    color: '#666',
    fontWeight: 600,
    margin: '0 0 10px 0',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  methodBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    width: '100%',
    padding: '14px 16px',
    background: '#faf7ff',
    border: '1.5px solid #e0d0f7',
    borderRadius: 12,
    cursor: 'pointer',
    marginBottom: 10,
    fontSize: 14,
    color: '#444',
    transition: 'border-color 0.15s',
  },
  methodBtnActive: {
    borderColor: '#5e06a7',
    background: '#f3e8ff',
  },
  methodIcon: { fontSize: 20 },
  methodLabel: { flex: 1, fontWeight: 600, textAlign: 'left' },
  methodCheck: { color: '#5e06a7', fontWeight: 700 },
  form: { marginTop: 16 },
  payBtn: {
    display: 'block',
    width: '100%',
    padding: '15px 0',
    marginTop: 20,
    background: '#5e06a7',
    color: '#fff',
    fontSize: 16,
    fontWeight: 700,
    border: 'none',
    borderRadius: 12,
    cursor: 'pointer',
    boxShadow: '0 4px 16px rgba(94,6,167,0.3)',
    transition: 'opacity 0.15s',
  },
  payBtnDisabled: {
    opacity: 0.55,
    cursor: 'not-allowed',
  },
  backBtn: {
    background: 'transparent',
    border: 'none',
    color: '#7c3aed',
    fontWeight: 600,
    fontSize: 14,
    cursor: 'pointer',
    padding: '0 0 16px 0',
    display: 'block',
  },
  errorText: {
    color: '#ef4444',
    fontSize: 13,
    margin: '8px 0 0 0',
    textAlign: 'center',
  },
  loadingText: {
    color: '#666',
    fontSize: 14,
    marginTop: 12,
  },
  lockBadge: {
    textAlign: 'center',
    color: '#999',
    fontSize: 12,
    marginTop: 20,
  },
  resultCard: {
    background: '#fff',
    borderRadius: 20,
    boxShadow: '0 8px 40px rgba(94,6,167,0.12)',
    padding: '40px 28px',
    width: '100%',
    maxWidth: 400,
    textAlign: 'center',
  },
  successIcon: {
    width: 64,
    height: 64,
    borderRadius: '50%',
    background: '#dcfce7',
    color: '#16a34a',
    fontSize: 28,
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px',
  },
  pendingIcon: {
    width: 64,
    height: 64,
    borderRadius: '50%',
    background: '#fef3c7',
    fontSize: 26,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px',
  },
  errorIcon: {
    width: 64,
    height: 64,
    borderRadius: '50%',
    background: '#fee2e2',
    color: '#dc2626',
    fontSize: 24,
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px',
  },
  resultTitle: {
    fontSize: 22,
    fontWeight: 800,
    color: '#1a1a1a',
    margin: '0 0 12px 0',
  },
  resultBody: {
    fontSize: 15,
    color: '#555',
    lineHeight: 1.5,
    margin: '0 0 20px 0',
  },
  txId: {
    fontSize: 13,
    color: '#999',
    marginBottom: 20,
  },
};

// Inject keyframe animation for spinner
const spinStyle = document.createElement('style');
spinStyle.textContent = `@keyframes spin { to { transform: rotate(360deg); } }`;
document.head.appendChild(spinStyle);
